import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSearchParams, useLocation } from "react-router-dom";
import { lookupGroup, requestJoinGroup } from "@/lib/groups";
import { ApiError } from "@/api/client";
import { toast } from "sonner";
import confetti from "canvas-confetti";

/**
 * Watches for ?code=XXXXXX in the URL or a `pending_invite_code` saved in
 * sessionStorage (set by the Login page). As soon as the user is signed
 * in, files a pending join request against that group — admins approve
 * via Gruppe → Anfragen.
 *
 * History: we briefly swapped this to a direct claim (no approval) for
 * faster onboarding. Reverted because group admins want gatekeeping —
 * the invite link is meant to surface the candidate, not auto-add them.
 */
export function InviteClaimer() {
  const { user, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (loading || !user || handledRef.current) return;
    // Onboarding has its own dedicated invite-code UI (step 2). Letting
    // InviteClaimer auto-file the request in parallel would race against
    // the onboarding flow and confuse the toast/state. The onboarding
    // page handles the request itself.
    if (location.pathname === "/onboarding") return;
    let code = (params.get("code") || "").toUpperCase().slice(0, 6);
    if (!code) {
      try { code = (sessionStorage.getItem("pending_invite_code") || "").toUpperCase(); } catch {}
    }
    if (!code || code.length !== 6) return;
    handledRef.current = true;

    (async () => {
      try {
        const preview = await lookupGroup(code);
        const res = await requestJoinGroup(code);
        try { sessionStorage.removeItem("pending_invite_code"); } catch {}
        if (params.get("code")) {
          params.delete("code");
          setParams(params, { replace: true });
        }
        const name = preview?.name ?? res.group_name;
        if (res.status === "already_member") {
          toast.info(`Du bist bereits Mitglied von „${name}"`);
        } else {
          // Mini confetti so the request still feels rewarding —
          // membership itself comes after admin approval.
          confetti({ particleCount: 40, spread: 60, origin: { y: 0.5 } });
          toast.success(
            `⏳ Anfrage an „${name}" gesendet — ein Admin der Gruppe muss dich noch bestätigen.`,
            { duration: 6000 },
          );
        }
      } catch (e) {
        if (params.get("code")) {
          const msg = e instanceof ApiError ? e.message : "Code konnte nicht eingelöst werden";
          toast.error(msg);
        }
      }
    })();
  }, [loading, user, params, setParams, location.pathname]);

  return null;
}
